/**
 * SRD HTML Field Mapping
 *
 * Field mapping for SRD 5.1 HTML templates (Classic and Compact).
 * Uses clean, semantic field names designed for HTML data-field attributes.
 *
 * Unlike the WotC mapping (which uses exact PDF form field names),
 * this mapping uses readable camelCase names that work well in HTML templates.
 */

import type { AbilityName, SkillName } from '@character-creator/types';
import type { CharacterSheetData, PdfFieldMapping } from '../types';
import { formatModifier } from '../../../utils/formatting';

// Ability score field names (readable camelCase)
const ABILITY_KEYS: AbilityName[] = [
  'strength',
  'dexterity',
  'constitution',
  'intelligence',
  'wisdom',
  'charisma',
];

// Short ability abbreviations for compact display
const ABILITY_ABBREV: Record<AbilityName, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

// Skill names mapped to camelCase field names
const SKILL_FIELD_MAP: Record<SkillName, string> = {
  'acrobatics': 'acrobatics',
  'animal-handling': 'animalHandling',
  'arcana': 'arcana',
  'athletics': 'athletics',
  'deception': 'deception',
  'history': 'history',
  'insight': 'insight',
  'intimidation': 'intimidation',
  'investigation': 'investigation',
  'medicine': 'medicine',
  'nature': 'nature',
  'perception': 'perception',
  'performance': 'performance',
  'persuasion': 'persuasion',
  'religion': 'religion',
  'sleight-of-hand': 'sleightOfHand',
  'stealth': 'stealth',
  'survival': 'survival',
};

// Skill display names for templates
const SKILL_DISPLAY_NAMES: Record<SkillName, string> = {
  'acrobatics': 'Acrobatics',
  'animal-handling': 'Animal Handling',
  'arcana': 'Arcana',
  'athletics': 'Athletics',
  'deception': 'Deception',
  'history': 'History',
  'insight': 'Insight',
  'intimidation': 'Intimidation',
  'investigation': 'Investigation',
  'medicine': 'Medicine',
  'nature': 'Nature',
  'perception': 'Perception',
  'performance': 'Performance',
  'persuasion': 'Persuasion',
  'religion': 'Religion',
  'sleight-of-hand': 'Sleight of Hand',
  'stealth': 'Stealth',
  'survival': 'Survival',
};

/**
 * Map CharacterSheetData to field mappings for SRD HTML templates.
 *
 * Field naming convention:
 * - Basic info: characterName, classLevel, background, race, alignment
 * - Abilities: strScore, strMod, dexScore, dexMod, etc.
 * - Combat: ac, initiative, speed, hpMax, hitDice, profBonus
 * - Saves: strSave, strSaveProf, dexSave, dexSaveProf, etc.
 * - Skills: acrobatics, acrobaticsProf, animalHandling, etc.
 * - Spells: spellClass, spellAbility, spellDC, spellAttack, cantrips, spells1st, etc.
 * - Conditionals: isSpellcaster, hasSpells1st, etc.
 */
export function mapSrdHtmlFields(data: CharacterSheetData): PdfFieldMapping[] {
  const mappings: PdfFieldMapping[] = [];

  // === BASIC INFO ===
  mappings.push({ fieldName: 'characterName', value: data.characterName, fieldType: 'text' });
  mappings.push({ fieldName: 'classLevel', value: data.classLevel, fieldType: 'text' });
  mappings.push({ fieldName: 'background', value: data.background, fieldType: 'text' });
  mappings.push({ fieldName: 'race', value: data.race, fieldType: 'text' });
  mappings.push({ fieldName: 'alignment', value: data.alignment, fieldType: 'text' });
  mappings.push({ fieldName: 'xp', value: data.experiencePoints, fieldType: 'text' });
  mappings.push({ fieldName: 'playerName', value: data.playerName, fieldType: 'text' });

  // === ABILITY SCORES ===
  for (const ability of ABILITY_KEYS) {
    const abbrev = ABILITY_ABBREV[ability].toLowerCase();
    const abilityData = data.abilities[ability];

    // Score (e.g., "14")
    mappings.push({
      fieldName: `${abbrev}Score`,
      value: String(abilityData.score),
      fieldType: 'text',
    });

    // Modifier with sign (e.g., "+2")
    mappings.push({
      fieldName: `${abbrev}Mod`,
      value: formatModifier(abilityData.modifier),
      fieldType: 'text',
    });
  }

  // === COMBAT STATS ===
  mappings.push({ fieldName: 'profBonus', value: formatModifier(data.proficiencyBonus), fieldType: 'text' });
  mappings.push({ fieldName: 'ac', value: String(data.armorClass), fieldType: 'text' });
  mappings.push({ fieldName: 'initiative', value: formatModifier(data.initiative), fieldType: 'text' });
  mappings.push({ fieldName: 'speed', value: `${data.speed} ft`, fieldType: 'text' });
  mappings.push({ fieldName: 'speedValue', value: String(data.speed), fieldType: 'text' });
  mappings.push({ fieldName: 'hpMax', value: String(data.hitPointMaximum), fieldType: 'text' });
  mappings.push({ fieldName: 'hpCurrent', value: data.hitPointCurrent, fieldType: 'text' });
  mappings.push({ fieldName: 'hpTemp', value: data.hitPointTemp, fieldType: 'text' });
  mappings.push({ fieldName: 'hitDice', value: data.hitDice, fieldType: 'text' });
  mappings.push({ fieldName: 'hitDiceTotal', value: data.hitDiceTotal, fieldType: 'text' });

  // === SAVING THROWS ===
  for (const ability of ABILITY_KEYS) {
    const abbrev = ABILITY_ABBREV[ability].toLowerCase();
    const saveBonus = data.savingThrowModifiers[ability];
    const isProficient = data.savingThrowProficiencies.includes(ability);

    // Save modifier (e.g., "+4")
    mappings.push({
      fieldName: `${abbrev}Save`,
      value: formatModifier(saveBonus),
      fieldType: 'text',
    });

    // Proficiency flag for CSS styling (e.g., "true" or "")
    mappings.push({
      fieldName: `${abbrev}SaveProf`,
      value: isProficient ? 'true' : '',
      fieldType: 'text',
    });
  }

  // === SKILLS ===
  const skillKeys = Object.keys(SKILL_FIELD_MAP) as SkillName[];
  for (const skill of skillKeys) {
    const fieldName = SKILL_FIELD_MAP[skill];
    const bonus = data.skillModifiers[skill];
    const isProficient = data.skillProficiencies.includes(skill);

    // Skill modifier (e.g., "+5")
    mappings.push({
      fieldName,
      value: formatModifier(bonus),
      fieldType: 'text',
    });

    // Proficiency flag for CSS styling
    mappings.push({
      fieldName: `${fieldName}Prof`,
      value: isProficient ? 'true' : '',
      fieldType: 'text',
    });

    // Display name for labels
    mappings.push({
      fieldName: `${fieldName}Name`,
      value: SKILL_DISPLAY_NAMES[skill],
      fieldType: 'text',
    });
  }

  // Passive Perception
  mappings.push({
    fieldName: 'passivePerception',
    value: String(data.passivePerception),
    fieldType: 'text',
  });

  // === PROFICIENCIES & LANGUAGES ===
  mappings.push({
    fieldName: 'proficienciesAndLanguages',
    value: data.proficienciesAndLanguages,
    fieldType: 'text',
  });

  // Split proficiencies for structured templates
  const profLines = data.proficienciesAndLanguages.split('\n');
  const languagesLine = profLines.find((l) => l.startsWith('Languages:'));
  const armorLine = profLines.find((l) => l.startsWith('Armor:'));
  const weaponsLine = profLines.find((l) => l.startsWith('Weapons:'));
  const toolsLine = profLines.find((l) => l.startsWith('Tools:'));

  mappings.push({
    fieldName: 'languages',
    value: languagesLine?.replace('Languages: ', '') || 'Common',
    fieldType: 'text',
  });
  mappings.push({
    fieldName: 'armorProf',
    value: armorLine?.replace('Armor: ', '') || 'None',
    fieldType: 'text',
  });
  mappings.push({
    fieldName: 'weaponProf',
    value: weaponsLine?.replace('Weapons: ', '') || 'None',
    fieldType: 'text',
  });
  mappings.push({
    fieldName: 'toolProf',
    value: toolsLine?.replace('Tools: ', '') || 'None',
    fieldType: 'text',
  });

  // === ATTACKS ===
  // Support up to 5 attacks for HTML templates
  for (let i = 0; i < 5; i++) {
    const attack = data.attacks[i];
    const num = i + 1;

    if (attack) {
      mappings.push({ fieldName: `attack${num}Name`, value: attack.name, fieldType: 'text' });
      mappings.push({ fieldName: `attack${num}Bonus`, value: attack.bonus, fieldType: 'text' });
      const damageWithType = attack.damageType
        ? `${attack.damage} ${attack.damageType}`
        : attack.damage;
      mappings.push({ fieldName: `attack${num}Damage`, value: damageWithType, fieldType: 'text' });
      mappings.push({ fieldName: `attack${num}Visible`, value: 'true', fieldType: 'text' });
    } else {
      mappings.push({ fieldName: `attack${num}Name`, value: '', fieldType: 'text' });
      mappings.push({ fieldName: `attack${num}Bonus`, value: '', fieldType: 'text' });
      mappings.push({ fieldName: `attack${num}Damage`, value: '', fieldType: 'text' });
      mappings.push({ fieldName: `attack${num}Visible`, value: '', fieldType: 'text' });
    }
  }

  // === EQUIPMENT ===
  mappings.push({ fieldName: 'equipment', value: data.equipment, fieldType: 'text' });
  mappings.push({ fieldName: 'cp', value: String(data.currency?.cp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'sp', value: String(data.currency?.sp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'ep', value: String(data.currency?.ep ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'gp', value: String(data.currency?.gp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'pp', value: String(data.currency?.pp ?? 0), fieldType: 'text' });

  // === PERSONALITY ===
  mappings.push({ fieldName: 'personalityTraits', value: data.personalityTraits, fieldType: 'text' });
  mappings.push({ fieldName: 'ideals', value: data.ideals, fieldType: 'text' });
  mappings.push({ fieldName: 'bonds', value: data.bonds, fieldType: 'text' });
  mappings.push({ fieldName: 'flaws', value: data.flaws, fieldType: 'text' });

  // === FEATURES & TRAITS ===
  mappings.push({ fieldName: 'featuresAndTraits', value: data.featuresAndTraits, fieldType: 'text' });
  mappings.push({ fieldName: 'additionalFeatures', value: data.additionalFeaturesTraits, fieldType: 'text' });

  // === BACKSTORY & APPEARANCE ===
  mappings.push({ fieldName: 'backstory', value: data.backstory, fieldType: 'text' });
  mappings.push({ fieldName: 'age', value: data.appearance.age, fieldType: 'text' });
  mappings.push({ fieldName: 'height', value: data.appearance.height, fieldType: 'text' });
  mappings.push({ fieldName: 'weight', value: data.appearance.weight, fieldType: 'text' });
  mappings.push({ fieldName: 'eyes', value: data.appearance.eyes, fieldType: 'text' });
  mappings.push({ fieldName: 'skin', value: data.appearance.skin, fieldType: 'text' });
  mappings.push({ fieldName: 'hair', value: data.appearance.hair, fieldType: 'text' });
  mappings.push({ fieldName: 'allies', value: data.allies, fieldType: 'text' });
  mappings.push({ fieldName: 'treasure', value: data.treasure, fieldType: 'text' });

  // === SPELLCASTING ===
  const isSpellcaster = !!data.spellcasting;
  mappings.push({
    fieldName: 'isSpellcaster',
    value: isSpellcaster ? 'true' : '',
    fieldType: 'text',
  });

  if (data.spellcasting) {
    mappings.push({
      fieldName: 'spellClass',
      value: data.spellcasting.spellcastingClass,
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'spellAbility',
      value: data.spellcasting.spellcastingAbility,
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'spellDC',
      value: String(data.spellcasting.spellSaveDC),
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'spellAttack',
      value: formatModifier(data.spellcasting.spellAttackBonus),
      fieldType: 'text',
    });

    // Spell slots per level (1-9)
    const spellSlots = data.spellcasting.spellSlots || [];
    for (let level = 1; level <= 9; level++) {
      const slotData = spellSlots[level - 1];
      const total = slotData?.total ?? 0;

      mappings.push({
        fieldName: `slots${level}`,
        value: String(total),
        fieldType: 'text',
      });

      // Flag for conditional visibility
      mappings.push({
        fieldName: `hasSlots${level}`,
        value: total > 0 ? 'true' : '',
        fieldType: 'text',
      });
    }

    // Spell lists by level (joined with bullet separator)
    const spellsByLevel = data.spellcasting.spellsByLevel || [];
    const levelNames = ['cantrips', 'spells1st', 'spells2nd', 'spells3rd', 'spells4th', 'spells5th', 'spells6th', 'spells7th', 'spells8th', 'spells9th'];

    for (let level = 0; level <= 9; level++) {
      const spells = spellsByLevel[level] || [];
      const joined = spells.join(' • ');

      mappings.push({
        fieldName: levelNames[level],
        value: joined,
        fieldType: 'text',
      });

      // Flag for conditional visibility
      mappings.push({
        fieldName: `has${levelNames[level].charAt(0).toUpperCase()}${levelNames[level].slice(1)}`,
        value: spells.length > 0 ? 'true' : '',
        fieldType: 'text',
      });
    }
  } else {
    // Empty spellcasting fields for martial characters
    mappings.push({ fieldName: 'spellClass', value: '', fieldType: 'text' });
    mappings.push({ fieldName: 'spellAbility', value: '', fieldType: 'text' });
    mappings.push({ fieldName: 'spellDC', value: '', fieldType: 'text' });
    mappings.push({ fieldName: 'spellAttack', value: '', fieldType: 'text' });

    for (let level = 1; level <= 9; level++) {
      mappings.push({ fieldName: `slots${level}`, value: '0', fieldType: 'text' });
      mappings.push({ fieldName: `hasSlots${level}`, value: '', fieldType: 'text' });
    }

    const levelNames = ['cantrips', 'spells1st', 'spells2nd', 'spells3rd', 'spells4th', 'spells5th', 'spells6th', 'spells7th', 'spells8th', 'spells9th'];
    for (const name of levelNames) {
      mappings.push({ fieldName: name, value: '', fieldType: 'text' });
      mappings.push({
        fieldName: `has${name.charAt(0).toUpperCase()}${name.slice(1)}`,
        value: '',
        fieldType: 'text',
      });
    }
  }

  return mappings;
}
